import { apiClient } from '@/shared/lib/axios';

export type InformationCreatePayload = {
  title: string;
  information_date: string;
  contents?: string | null;
  display_from?: string | null;
  display_to?: string | null;
  is_display: boolean;
  service_type?: string | null;
  tenant_ids: number[];
  clinic_ids?: number[];
};

export type AdminInformationClinicDetail = {
  id: number;
  clinic_number: string;
  site_type: string | null;
};

export type AdminInformationItem = {
  id: number;
  title: string;
  information_date: string | null;
  contents: string | null;
  display_from: string | null;
  display_to: string | null;
  is_display: boolean;
  service_type: string | null;
  tenant_ids: number[];
  tenant_names: string[];
  clinic_ids: number[];
  clinic_details?: AdminInformationClinicDetail[];
  created_at: string | null;
  updated_at: string | null;
};

export type AdminInformationResponse = {
  success: boolean;
  data: AdminInformationItem;
  message?: string;
};

export type InformationListParams = {
  search_information_date?: string;
  search_title?: string;
  search_contents?: string;
  available_date?: string;
  search_site_type?: string[];
  search_service_type?: string[];
  sort?: [string, string];
  page?: number;
  per_page?: number;
};

export type InformationListMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

export type InformationListResponse = {
  success: boolean;
  data: AdminInformationItem[];
  meta: InformationListMeta;
};

export const getInformationList = async (
  siteType: string,
  params: InformationListParams
): Promise<InformationListResponse> => {
  const response = await apiClient.get<InformationListResponse>(
    `/admin/${siteType}/informations`,
    { params }
  );
  return response.data;
};

export const createInformation = async (
  siteType: string,
  data: InformationCreatePayload
): Promise<AdminInformationResponse> => {
  const response = await apiClient.post<AdminInformationResponse>(
    `/admin/${siteType}/informations`,
    data
  );
  return response.data;
};

export const getInformation = async (
  siteType: string,
  id: number
): Promise<AdminInformationResponse> => {
  const response = await apiClient.get<AdminInformationResponse>(
    `/admin/${siteType}/informations/${id}`
  );
  return response.data;
};

export type DeleteInformationResponse = {
  success: boolean;
  message?: string;
};

export const deleteInformation = async (
  siteType: string,
  id: number
): Promise<DeleteInformationResponse> => {
  const response = await apiClient.delete<DeleteInformationResponse>(
    `/admin/${siteType}/informations/${id}`
  );
  return response.data;
};

export const updateInformation = async (
  siteType: string,
  id: number,
  data: InformationCreatePayload
): Promise<AdminInformationResponse> => {
  const response = await apiClient.put<AdminInformationResponse>(
    `/admin/${siteType}/informations/${id}`,
    data
  );
  return response.data;
};

export const exportCsvInformations = async (
  siteType: string,
  params: Omit<InformationListParams, 'page' | 'per_page'>
): Promise<Blob> => {
  const response = await apiClient.get(
    `/admin/${siteType}/informations/csv`,
    { params, responseType: 'blob' }
  );
  return response.data as Blob;
};

